/**
 * 50/50 client-side A/B redirect used by the four standard landing pages
 * that have VSL variants.
 *
 * On first paint we roll a coin, persist the assignment in sessionStorage
 * (so if the user hits Back or refreshes we don't reroll), then
 * ``location.replace()`` to either the v1 or v2 URL — carrying the current
 * query string (UTMs, gclid, fbclid, etc.) with us so ad attribution stays
 * intact end-to-end.
 *
 * Usage inside a page component:
 *
 *     import { useAbRedirect } from "@/lib/useAbRedirect";
 *     useAbRedirect("/foo-vsl", "/foo-vsl-v2", "foo");
 */
import { useEffect } from "react";

export function useAbRedirect(vslPath, vsl2Path, key) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Escape hatch: if the URL already contains ?ab=off (e.g. QA testing
    // the base page) we skip the redirect entirely for this session.
    const params = new URLSearchParams(window.location.search);
    if (params.get("ab") === "off") return;

    const storageKey = `jfl_ab_${key}`;
    let variant = sessionStorage.getItem(storageKey);
    if (variant !== "v1" && variant !== "v2") {
      variant = Math.random() < 0.5 ? "v1" : "v2";
      try {
        sessionStorage.setItem(storageKey, variant);
      } catch (_) {
        /* private mode / storage disabled — variant still applies for this render */
      }
    }
    const target = variant === "v1" ? vslPath : vsl2Path;
    window.location.replace(`${target}${window.location.search}${window.location.hash}`);
  }, [vslPath, vsl2Path, key]);
}
